import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/analytics"

// Tracks media playback events for a Vimeo iframe. Attach to the iframe's
// parent element (or any ancestor) and pass the media item id + (optional)
// feature id as data values. The controller looks for an <iframe> child
// whose src points at vimeo.com / player.vimeo.com.
//
// Events:
//   media_play     — first play of this media instance
//   media_progress — once per crossed bucket (25, 50, 75)
//   media_complete — fired on `ended`
//
// If no Vimeo iframe is found (or the @vimeo/player package is unavailable),
// the controller becomes a no-op. Other media types can be added later.
export default class extends Controller {
  static values = {
    mediaItemId: String,
    featureId: String
  }

  async connect() {
    if (!this.hasMediaItemIdValue || !this.mediaItemIdValue) return

    const iframe = this.element.tagName === "IFRAME"
      ? this.element
      : this.element.querySelector("iframe")
    if (!iframe) return

    const src = iframe.getAttribute("src") || ""
    if (!/vimeo\.com|player\.vimeo\.com/.test(src)) return

    let Player
    try {
      Player = (await import("@vimeo/player")).default
    } catch (_) {
      // Package missing or failed to load — silently bail.
      return
    }

    try {
      this.player = new Player(iframe)
    } catch (_) {
      return
    }

    this.firedThresholds = new Set()
    this.playFired = false

    this.onPlay = this.onPlay.bind(this)
    this.onTimeUpdate = this.onTimeUpdate.bind(this)
    this.onEnded = this.onEnded.bind(this)

    this.player.on("play", this.onPlay)
    this.player.on("timeupdate", this.onTimeUpdate)
    this.player.on("ended", this.onEnded)
  }

  disconnect() {
    if (!this.player) return
    try {
      this.player.off("play", this.onPlay)
      this.player.off("timeupdate", this.onTimeUpdate)
      this.player.off("ended", this.onEnded)
    } catch (_) { /* silent */ }
  }

  baseProps() {
    const props = { media_item_id: this.mediaItemIdValue }
    if (this.hasFeatureIdValue && this.featureIdValue) {
      props.feature_id = this.featureIdValue
    }
    return props
  }

  onPlay() {
    if (this.playFired) return
    this.playFired = true
    track("media_play", this.baseProps())
  }

  onTimeUpdate(data) {
    if (!data || !data.duration || data.duration <= 0) return
    const percent = (data.seconds / data.duration) * 100
    for (const threshold of [25, 50, 75]) {
      if (percent >= threshold && !this.firedThresholds.has(threshold)) {
        this.firedThresholds.add(threshold)
        track("media_progress", { ...this.baseProps(), percent: threshold })
      }
    }
  }

  onEnded() {
    track("media_complete", this.baseProps())
  }
}
